import { normalizeWoodType, DEFAULT_FABRIC_CODE } from '../utils/constants.js';
import { activeProductCode } from '../utils/variants.js';

/**
 * One entry per orderable item in scene — read these rather than re-parsing the link's string.
 * @returns {Array<Object>|null} productCode, fabricCode, legColor, legHeight, covers, boardColor
 */
export function collectOrderEntries(app) {
  try {
    const furnitureManager = app.getFurnitureManager();
    if (!furnitureManager) {
      console.error('Failed to generate order link: FurnitureManager not available');
      return null;
    }

    const furnitureInScene = furnitureManager.getAllFurniture();
    if (furnitureInScene.length === 0) {
      console.warn('No furniture in scene to generate order link');
      return null;
    }

    // Get current leg texture type ("dark" or "light")
    const currentLegType = furnitureManager.currentLegTextureType || 'light';

    const entries = [];

    for (const furniture of furnitureInScene) {
      const furnitureData = furniture.furnitureData;
      if (!furnitureData || !furnitureData.urlCode) {
        console.warn('Furniture missing urlCode:', furniture);
        continue;
      }

      // Skip furniture from collections with cart disabled
      if (!furnitureManager.isCartEnabledForFurniture(furnitureData)) {
        continue;
      }

      const entry = {
        productCode: activeProductCode(furniture),
        fabricCode: null,
        legColor: null,
        legHeight: null,
        covers: null,
        boardColor: null,
      };

      // Check if furniture uses fabric
      const hasFabric = furnitureData.properties?.materialZones?.includes('fabric');

      if (hasFabric) {
        // Use fabricCode (e.g., "ET-14") if available, otherwise try to look it up
        let fabricCode = furniture.fabricCode;

        // If no fabricCode but has fabricId, look it up
        if (!fabricCode && furniture.fabricId) {
          const materialManager = app.getMaterialManager();
          if (materialManager) {
            const fabricData = materialManager.fabrics[furniture.fabricId];
            if (fabricData && fabricData.fabricCode) {
              fabricCode = fabricData.fabricCode;
            }
          }
        }

        if (fabricCode) {
          entry.fabricCode = fabricCode;
        } else {
          // Use default fabric if not assigned
          // Try to get from parent if it's an attached item
          let fabricToUse = null;

          if (furniture.attachedTo && furniture.attachedTo.parentFurniture) {
            fabricToUse = furniture.attachedTo.parentFurniture.fabricId;
          }

          // If still no fabric, use materialManager's current selection or default
          if (!fabricToUse) {
            const materialManager = app.getMaterialManager();
            const uiManager = app.getUIManager();

            if (uiManager && uiManager.selectedMaterial && materialManager) {
              // Try to get fabric code synchronously from loaded materials
              const fabricData = materialManager.fabrics[uiManager.selectedMaterial];
              if (fabricData && fabricData.fabricCode) {
                fabricToUse = fabricData.fabricCode;
              }
            }

            // Final fallback to default fabric
            if (!fabricToUse) {
              fabricToUse = DEFAULT_FABRIC_CODE;
            }
          }

          console.warn(`Furniture missing fabricId, using: ${fabricToUse}`, furniture);
          entry.fabricCode = fabricToUse;
        }
      }

      // Check if the collection supports adjustable legs (furniture.json `hasLegs`) and
      // whether leg textures are enabled. Items in `hasLegs: false` collections (e.g. bonbon3,
      // zip) emit no `+lc` / `+ll` tokens regardless of their per-item materialZones.
      // Additionally gate on the *item's* materialZones: backrests, cushions, panels and
      // accessories live in legged collections (e.g. diagon, cascais) but have no legs of
      // their own (no `legs` zone), so they must not emit `+lc` / `+ll`.
      const collections = furnitureManager.getFurnitureCollections();
      const collectionHasLegs = collections?.[furnitureData.collection]?.hasLegs === true;
      const itemHasLegs = furnitureData.properties?.materialZones?.includes('legs') === true;
      const legOptionsEnabled = collectionHasLegs && itemHasLegs && furnitureManager.isLegTexturesEnabled(furnitureData);

      if (legOptionsEnabled) {
        entry.legColor = currentLegType;
        entry.legHeight = furnitureManager.currentLegHeight || '17';
      }

      // Per-product cover, hasCovers collections only; value validated per product (see getCoverForProduct).
      const collectionHasCovers = collections?.[furnitureData.collection]?.hasCovers === true;
      entry.covers = collectionHasCovers
        ? furnitureManager.getCoverForProduct(entry.productCode)
        : null;

      // `wood` zone items only — the set the backend grants boardColor in product-variations.json.
      const itemHasWood = furnitureData.properties?.materialZones?.includes('wood') === true;
      if (itemHasWood) {
        entry.boardColor = normalizeWoodType(furniture.woodType || furnitureManager.currentWoodType);
      }

      entries.push(entry);
    }

    if (entries.length === 0) {
      console.error('No valid furniture items to generate order link');
      return null;
    }

    return entries;
  } catch (error) {
    console.error('Failed to generate order link:', error);
    return null;
  }
}

/**
 * One order entry as the backend reads it: product code, then its option tokens in this order.
 * @param {Object} entry from collectOrderEntries()
 * @returns {string}
 */
export function formatOrderEntry(entry) {
  let out = entry.productCode;
  if (entry.fabricCode) out += `+fa${entry.fabricCode}`;
  // lcL for light, lcD for dark.
  if (entry.legColor) out += `+lc${entry.legColor === 'light' ? 'L' : 'D'}`;
  if (entry.legHeight) out += `+ll${entry.legHeight}`;
  if (entry.covers) out += `+covers=${entry.covers}`;
  if (entry.boardColor) out += `+boardColor=${entry.boardColor}`;
  return out;
}

/**
 * The order as a query string, e.g. `oiko-ts+faET-14+boardColor=dark&oiko-wst+boardColor=light`.
 * @returns {String|null}
 */
export function formatOrderLink(entries) {
  return entries?.length ? entries.map(formatOrderEntry).join('&') : null;
}
